'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
  Cell,
} from 'recharts';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';

interface TooltipPayload {
  payload: {
    country: string;
    access: number;
    program: string;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-surface-card border border-surface-border rounded-lg px-3 py-2 shadow-card-md text-xs">
      <p className="font-semibold text-text-main mb-1">{d?.country}</p>
      <p className="text-text-muted">
        Program:{' '}
        <span className="font-semibold" style={{ color: 'var(--geo)' }}>
          {d?.program}
        </span>
      </p>
      <p className="text-text-muted">
        Access:{' '}
        <span className="font-bold text-status-green">{d?.access}%</span>
      </p>
      <p className="text-text-muted">
        Remaining gap:{' '}
        <span className="font-medium text-status-red">
          {100 - (d?.access || 0)}%
        </span>
      </p>
    </div>
  );
}

export default function ConnectionsBarChart() {
  const { allCountries, profile } = useCountry();
  const { lang } = useLanguage();

  const chartData = allCountries.map((c) => ({
    country: c.country === "Cote d'Ivoire" ? "Cote d'Ivoire" : c.country,
    access: c.accessPercent,
    gap: 100 - c.accessPercent,
    program: c.program,
  }));

  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 16, right: 8, left: -20, bottom: 0 }}
          barSize={28}
        >
          <defs>
            <linearGradient id="connAccessGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--status-green)" stopOpacity={1} />
              <stop offset="100%" stopColor="var(--status-green)" stopOpacity={0.65} />
            </linearGradient>
            <linearGradient id="connAccessGradSelected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity={1} />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.65} />
            </linearGradient>
            <linearGradient id="connGapGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--muted)" stopOpacity={0.7} />
              <stop offset="100%" stopColor="var(--muted)" stopOpacity={0.3} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            vertical={false}
          />
          <XAxis
            dataKey="country"
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            interval={0}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(0,0,0,0.03)' }}
          />
          <ReferenceLine
            y={100}
            stroke="var(--accent)"
            strokeDasharray="5 4"
            strokeWidth={1.5}
            label={{
              value: '100% Target',
              position: 'insideTopRight',
              fontSize: 9,
              fill: 'var(--accent)',
            }}
          />
          <Bar dataKey="access" stackId="b" radius={[0, 0, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-conn-${index}`}
                fill={entry.country === profile.name ? 'url(#connAccessGradSelected)' : 'url(#connAccessGrad)'}
              />
            ))}
            <LabelList
              dataKey="access"
              position="insideTop"
              style={{ fontSize: '10px', fill: 'white', fontWeight: 700 }}
              formatter={(v: number) => `${v}%`}
            />
          </Bar>
          <Bar
            dataKey="gap"
            stackId="b"
            fill="url(#connGapGrad)"
            radius={[3, 3, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}